'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@vsys/shared';
import { Loader2, ArrowRight, ShieldCheck, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

export default function AccountantLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { login } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL || 'https://api.vsys.pro/api/v1'}/auth/login`, { credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.message || 'Credenciais inválidas');
      }

      // Se quiser bloquear login apenas para usuários do tipo contador, poderia fazer:
      // if (!data.user.accountingFirmId && !data.user.isSuperAdmin) {
      //   throw new Error('Acesso restrito apenas para contadores.');
      // }
      
      login(data.accessToken, data.user);
      toast.success('Bem-vindo ao Portal do Contador!');
      router.push('/dashboard');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1D] flex relative overflow-hidden font-sans">
      {/* Background Effects */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-fuchsia-500/20 rounded-full blur-[120px] pointer-events-none" />

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center relative z-10 p-6">
        <div className="w-full max-w-md">
          {/* Logo Area */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-white/[0.03] border border-white/[0.08] rounded-2xl flex items-center justify-center mb-4 shadow-[0_0_30px_rgba(255,255,255,0.05)]">
              <ShieldCheck className="w-8 h-8 text-indigo-400" />
            </div>
            <h1 className="text-2xl font-black text-white tracking-wider uppercase mb-1">
              VSYS <span className="text-indigo-400">Portal</span>
            </h1>
            <p className="text-sm text-slate-400">Área exclusiva para Contabilidades</p>
          </div>

          {/* Login Card */}
          <div className="bg-white/[0.02] border border-white/[0.08] rounded-3xl p-8 backdrop-blur-xl shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-indigo-500 opacity-50" />
            
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">E-mail Profissional</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full bg-white/[0.03] border border-white/[0.08] text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-indigo-500/50 transition-all placeholder:text-slate-600"
                  placeholder="contador@empresa.com.br"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Senha de Acesso</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full bg-white/[0.03] border border-white/[0.08] text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-indigo-500/50 transition-all placeholder:text-slate-600"
                  placeholder="••••••••"
                  required
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  className="w-full bg-indigo-500 hover:bg-indigo-400 text-white font-bold rounded-xl px-4 py-3.5 text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50 group-hover:shadow-[0_0_20px_rgba(99,102,241,0.2)]"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Acessar Portal
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Footer Info */}
          <div className="mt-8 text-center flex flex-col items-center gap-2">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <FileSpreadsheet className="w-4 h-4" />
              <span>Gerencie os XMLs fiscais de seus clientes</span>
            </div>
            <p className="text-[10px] text-slate-600 uppercase tracking-widest mt-4">
              VSYS ERP © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
